/** The message menu's Copy link / Pin / Mark unread for a conversation or thread screen (docs/design/slack-gap.md). */
import type { Message } from "@bundle/contracts";
import { Share, type ChannelViewModel, type WorkspaceCache } from "@bundle/core";
import { openBlock, openRemind, openReport, openShare, useAnnouncer, useNavigation, useLater, useSettingsContext, useT, type MessageExtras } from "@bundle/ui";
import { useSyncExternalStore } from "react";

/** `extrasFor(message)` → the MessageRow `extras` prop. */
export function useMessageExtras(vm: ChannelViewModel | undefined, cache: WorkspaceCache | undefined): (m: Message) => MessageExtras | undefined {
  const t = useT();
  const announce = useAnnouncer();
  const nav = useNavigation();
  const settings = useSettingsContext();
  // Later (saved messages + reminders): re-render when its items change ("Saved for later" lines).
  const later = useLater();
  useSyncExternalStore(later?.subscribe ?? noSubscribe, () => later?.state.items);
  // The shell's copy + web origin when it provides them (Settings context), else this page's.
  const copyAppLink = (path: string): Promise<void> => {
    const url = `${settings?.origin ?? location.origin}${path}`;
    return settings === null || settings === undefined ? navigator.clipboard.writeText(url) : settings.copy(url);
  };
  return (m) => {
    if (vm === undefined) return undefined;
    const extras: MessageExtras = {};
    const link = vm.messageLink(m.id);
    if (link !== undefined) extras.onCopyLink = () => void copyAppLink(link).then(() => announce(t("message.linkCopied")), () => undefined);
    if (vm.canPin(m.id)) extras.pin = { pinned: m.pinnedBy !== undefined, onToggle: () => vm.togglePin(m.id) };
    if (vm.canMarkUnread(m.id)) extras.onMarkUnread = () => vm.markUnread(m.id);
    if (later !== undefined && !m.id.startsWith("pending") && m.seq > 0) {
      const saved = later.savedItem(m.id);
      extras.later = {
        saved: saved?.state === "in_progress",
        onToggle: () => announce(t(later.toggleSaved(m.id, m.channelId) ? "later.saved" : "later.removed")),
      };
      extras.onRemind = () => openRemind({ kind: "message", messageId: m.id, channelId: m.channelId });
    }
    if (Share.canShare(m)) extras.onShare = () => openShare(m);
    if (m.shared !== undefined && cache !== undefined) {
      const url = Share.sharedSource(m.shared, cache).url;
      if (url !== undefined) extras.onOpenShared = () => nav.open(url);
    }
    if (m.unfurls !== undefined && vm.canHideUnfurl(m.id)) extras.onHideUnfurl = (url) => vm.hideUnfurl(m.id, url);
    if (m.pinnedBy !== undefined) extras.pinnedByName = cache?.members[m.pinnedBy]?.displayName ?? m.pinnedBy;
    // Report & block (logic/moderation): someone else's sent message; Block only while they aren't blocked yet.
    if (vm.canReport(m.id)) extras.onReport = () => openReport(m.id);
    const author = vm.blockableAuthor(m.id);
    if (author !== undefined && !author.blocked) extras.block = { name: author.name, onBlock: () => openBlock(author.userId, author.name) };
    return extras;
  };
}

const noSubscribe = () => () => {};
